// src/components/SanityBlogStrip.tsx
import { useEffect, useMemo, useState } from "react";
import { client } from "../sanity/client";
import { POSTS_QUERY_LIMITED } from "../sanity/queries";
import Card from "../components/Resources/CardMain";
import { formatDateTime } from "../shared/util/util";
import { Category, NEWS_BLOGS_UNION, Post, TAGS } from "./constants";

const hasAny = (arr: string[], allowed: string[]) =>
  arr.some((t) => allowed.includes(t));

export default function SanityBlogStrip({
  category = "All",
}: {
  category?: Category;
}) {
  const [posts, setPosts] = useState<Post[]>([]);

  useEffect(() => {
    client.fetch<Post[]>(POSTS_QUERY_LIMITED).then((r) => setPosts(r || []));
  }, []);

  // normalize + TRIM tags
  const normalized = useMemo(
    () =>
      posts.map((p) => ({
        ...p,
        _tags: (p.tags || []).map((t) => (t || "").trim().toLowerCase()),
      })),
    [posts]
  );

  const visible = useMemo(() => {
    switch (category) {
      case "AI":
        return normalized.filter((p) => hasAny(p._tags, TAGS.ai));
      case "Product":
        return normalized.filter((p) => hasAny(p._tags, TAGS.product));
      case "Blogs":
        return normalized.filter((p) => hasAny(p._tags, TAGS.blogs));
      case "News":
        return normalized.filter((p) => hasAny(p._tags, TAGS.news));
      case "Customer Gallery":
        return normalized.filter((p) => hasAny(p._tags, TAGS.customer));
      case "News & Blogs": // ← NEW: combined pill
        return normalized.filter(
          (p) => hasAny(p._tags, NEWS_BLOGS_UNION) || p._tags.length === 0 // include untagged if you like
        );
      case "All":
      default:
        // ORIGINAL (shows everything, including case studies):
        // return normalized;

        // UPDATED: hide customer / case-study posts from "All" on Resources page.
        // If you ever want them back, just replace this line with `return normalized;`
        // or uncomment the line above and delete this one.
        return normalized.filter((p) => !hasAny(p._tags, TAGS.customer));
    }
  }, [category, normalized]);

  return (
    <div className="mx-auto grid w-full max-w-max items-start justify-evenly gap-4 px-2 pb-8 sm:grid-cols-2 md:gap-y-5 md:px-0 lg:grid-cols-4">
      {visible.map((post) => (
        <div
          key={post._id}
          className="h-[34rem] w-full max-w-80 rounded-md border border-gray-500"
        >
          <Card
            link={
              hasAny(post._tags, TAGS.customer)
                ? `/customers/${post?.slug}`
                : `/blog/${post?.slug}`
            }
            title={post?.title}
            type={category != "All" ? category : post._tags[0]}
            image={{
              src: post?.coverImage?.asset?.url,
              width: post?.coverImage?.asset?.metadata?.dimensions?.width,
              height: post?.coverImage?.asset?.metadata?.dimensions?.height,
            }}
            datePublished={formatDateTime(post?.date)}
          />
        </div>
      ))}
    </div>
  );
}
